// Stacked rotate cards: a dealt stack that expands into a rotating fan, with a detail overlay.
// Initialised per section instance so several can coexist on one page.
const initStackedRotateCards = (root) => {
    if (!root || root.dataset.srcReady === 'true') return;
    root.dataset.srcReady = 'true';

    const cards = Array.prototype.slice.call(root.querySelectorAll('[data-src-card]'));
    const deck = root.querySelector('[data-src-deck]');
    const expandButton = root.querySelector('[data-src-expand]');
    const overlay = root.querySelector('[data-src-overlay]');
    const closeButton = root.querySelector('[data-src-close]');
    const blurb = root.querySelector('[data-src-blurb]');
    const textCard = root.querySelector('[data-src-text-card]');
    const blurbWrap = root.querySelector('[data-src-blurb-wrap]');
    const detailImage = root.querySelector('[data-src-detail-image]');
    const brandCard = root.querySelector('[data-src-brand-card]');
    const productHost = root.querySelector('[data-src-products]');
    const shopLink = root.querySelector('[data-src-shop]');
    const stage = root.querySelector('.stacked-rotate-cards__stage');
    let activeIndex = 0;
    let autoplayTimer;
    let dealTimer;
    const autoplayDelay = parseInt(root.dataset.autoplayDelay, 10) || 2600;
    let swipeStartX = 0;
    let swipeStartY = 0;
    let swiping = false;
    let suppressClick = false;
    let swipeResumeTimer;
    const mobileQuery = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;

    if (!cards.length) {
      if (expandButton) expandButton.disabled = true;
      return;
    }

    function normaliseOffset(offset) {
      const total = cards.length;
      const half = total / 2;
      if (offset > half) return offset - total;
      if (offset < -half) return offset + total;
      return offset;
    }

    function resetMobileScroll() {
      if (!deck || !window.matchMedia || !window.matchMedia('(max-width: 749px)').matches) return;
      deck.scrollLeft = 0;
    }

    function randomiseInitialRotations() {
      cards.forEach(function (card) {
        if (card.dataset.initial !== 'true') return;
        const baseRotate = parseFloat(card.style.getPropertyValue('--src-base-rotate')) || 0;
        const finalRotate = baseRotate + (Math.random() * 9) - 4.5;
        card.style.setProperty('--src-dealt-rotate', finalRotate.toFixed(2) + 'deg');
        card.style.setProperty('--src-initial-rotate', (finalRotate * 0.35).toFixed(2) + 'deg');
      });
    }

    function dealInitialCards() {
      window.clearTimeout(dealTimer);
      if (root.classList.contains('is-expanded') || root.classList.contains('is-dealt')) return;
      resetMobileScroll();
      randomiseInitialRotations();
      root.classList.add('is-dealt');
      dealTimer = window.setTimeout(function () {
        root.classList.add('is-dealt-complete');
      }, 1500);
    }

    function resetInitialCards() {
      if (root.classList.contains('is-expanded')) return;
      window.clearTimeout(dealTimer);
      clearInitialActive();
      root.classList.remove('is-dealt-complete', 'is-dealt');
      resetMobileScroll();
    }

    function setInitialActive(activeCard) {
      if (root.classList.contains('is-expanded') || !root.classList.contains('is-dealt-complete')) return;
      cards.forEach(function (card) {
        if (card.dataset.initial !== 'true') return;
        card.classList.toggle('is-active', card === activeCard);
        card.classList.toggle('is-not-active', card !== activeCard);
      });
    }

    function clearInitialActive() {
      cards.forEach(function (card) {
        card.classList.remove('is-active', 'is-not-active');
      });
    }

    function renderFan() {
      // Desktop fans wider: 4 cards each side (7 full + 2 faded edges). Mobile
      // keeps the tighter 3-each-side spread so cards don't sliver off-screen.
      const isMobile = window.matchMedia ? window.matchMedia('(max-width: 749px)').matches : false;
      const edgeIndex = isMobile ? 3 : 4;
      cards.forEach(function (card, index) {
        const offset = normaliseOffset(index - activeIndex);
        const absOffset = Math.abs(offset);
        const visibleOffset = Math.max(-7, Math.min(7, offset));
        const visibleAbsOffset = Math.abs(visibleOffset);
        // Bigger central card with a steeper falloff = more depth/focus; the
        // outer cards recede smaller. (Still >= dealt scales for the central 5.)
        const scale = 1.16 - Math.min(visibleAbsOffset, 7) * 0.045;
        const mobileScale = 1 - Math.min(visibleAbsOffset, 4) * 0.035;
        // 11deg/card (was 10) tilts the edge cards more for a rounder carousel;
        // fanRadiusX trimmed to 80 so the steeper angle keeps ~the same spread,
        // fanRadiusY raised to 68 so the arc droops more (more circular).
        const angle = visibleOffset * 11;
        const angleRad = angle * Math.PI / 180;
        const fanRadiusX = 80;
        const fanRadiusY = 68;
        const mobileRadius = 24;
        const fanX = Math.sin(angleRad) * fanRadiusX;
        const fanY = (1 - Math.cos(angleRad)) * fanRadiusY;
        const mobileFanX = Math.sin(angleRad) * mobileRadius;
        const mobileFanY = (1 - Math.cos(angleRad)) * mobileRadius;
        const opacity = absOffset > edgeIndex ? 0 : (absOffset > (edgeIndex - 1) ? 0.55 : 1);

        card.style.setProperty('--src-fan-x', 'clamp(-58rem, ' + fanX.toFixed(2) + 'rem, 58rem)');
        card.style.setProperty('--src-fan-y', 'calc(var(--src-fan-lift) + ' + fanY.toFixed(2) + 'rem)');
        card.style.setProperty('--src-fan-rotate', angle.toFixed(2) + 'deg');
        card.style.setProperty('--src-fan-scale', scale.toFixed(3));
        card.style.setProperty('--src-fan-opacity', opacity);
        card.style.setProperty('--src-mobile-fan-x', 'clamp(-18rem, ' + mobileFanX.toFixed(2) + 'rem, 18rem)');
        card.style.setProperty('--src-mobile-fan-y', 'calc(var(--src-fan-lift) + ' + mobileFanY.toFixed(2) + 'rem)');
        card.style.setProperty('--src-mobile-fan-rotate', angle.toFixed(2) + 'deg');
        card.style.setProperty('--src-mobile-fan-scale', mobileScale.toFixed(3));
        card.style.pointerEvents = opacity === 0 ? 'none' : 'auto';
        const isCurrent = index === activeIndex;
        card.style.zIndex = isCurrent ? '160' : String(100 - visibleAbsOffset);
        card.classList.toggle('is-current', isCurrent);
        card.dataset.edgeCard = absOffset > (edgeIndex - 1) && absOffset <= edgeIndex ? 'true' : 'false';
      });
    }

    function startAutoplay() {
      window.clearInterval(autoplayTimer);
      if (!root.classList.contains('is-expanded')) return;
      autoplayTimer = window.setInterval(function () {
        if (root.classList.contains('is-detail-open')) return;
        activeIndex = (activeIndex + 1) % cards.length;
        renderFan();
      }, autoplayDelay);
    }

    function stopAutoplay() {
      window.clearInterval(autoplayTimer);
    }

    function lockPageScroll() {
      // Skip in the theme editor so the merchant can still scroll the preview.
      if (window.Shopify && window.Shopify.designMode) return;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }

    function unlockPageScroll() {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }

    const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';
    let detailTrigger = null;

    // The overlay is modal: focus must enter it, stay inside it, and go back to whatever opened it.
    function trapDetailFocus(event) {
      if (event.key !== 'Tab' || !root.classList.contains('is-detail-open')) return;
      const focusable = Array.prototype.slice
        .call(overlay.querySelectorAll(FOCUSABLE))
        .filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    // The card renders a small srcset candidate, so the 900px popup asset is never fetched by the
    // card itself. Warm it on intent (hover or focus) so the detail view does not flash empty.
    const warmDetailImage = (card) => {
      const src = card.dataset.cardImage;
      if (!src || card.dataset.warmed === 'true') return;
      card.dataset.warmed = 'true';
      const preload = new Image();
      preload.decoding = 'async';
      preload.src = src;
    };
    cards.forEach((card) => {
      card.addEventListener('pointerenter', () => warmDetailImage(card));
      card.addEventListener('focus', () => warmDetailImage(card));
    });

    function openDetail(card) {
      stopAutoplay();
      const useMobile = mobileQuery ? mobileQuery.matches : false;
      const alignMap = {
        top: 'flex-start',
        center: 'center',
        bottom: 'flex-end'
      };
      blurb.textContent = card.dataset.blurb || (root.dataset.defaultBlurb || '');
      if (blurbWrap) {
        const alignValue = useMobile
          ? (card.dataset.blurbAlignMobile || card.dataset.blurbAlign)
          : card.dataset.blurbAlign;
        blurbWrap.style.justifyContent = alignMap[alignValue] || 'center';
      }

      const blurbSize = useMobile
        ? (card.dataset.blurbSizeMobile || card.dataset.blurbSize)
        : card.dataset.blurbSize;
      blurb.style.fontSize = blurbSize ? blurbSize + 'px' : '';

      if (textCard) {
        const textPad = useMobile
          ? (card.dataset.textPadMobile || card.dataset.textPad)
          : card.dataset.textPad;
        textCard.style.padding = textPad ? textPad + 'px' : '';
      }

      // Secondary card just reuses the front card's own image asset.
      if (card.dataset.cardImage) {
        detailImage.decoding = 'async';
        detailImage.alt = card.dataset.title || '';
        detailImage.src = card.dataset.cardImage;
        detailImage.hidden = false;
      } else {
        detailImage.removeAttribute('src');
        detailImage.hidden = true;
      }

      productHost.innerHTML = '';
      for (let i = 1; i <= 4; i += 1) {
        const src = card.getAttribute('data-product-' + i);
        if (!src) continue;
        const img = document.createElement('img');
        img.className = 'stacked-rotate-cards__product';
        img.src = src;
        img.alt = '';
        img.style.setProperty('--src-product-x', ((useMobile && card.getAttribute('data-product-' + i + '-mobile-x')) || card.getAttribute('data-product-' + i + '-x') || 0) + '%');
        img.style.setProperty('--src-product-y', ((useMobile && card.getAttribute('data-product-' + i + '-mobile-y')) || card.getAttribute('data-product-' + i + '-y') || 0) + '%');
        img.style.setProperty('--src-product-size', ((useMobile && card.getAttribute('data-product-' + i + '-mobile-size')) || card.getAttribute('data-product-' + i + '-size') || 120) + 'px');
        img.style.setProperty('--src-product-rotate', (((i * 17) % 29) - 14) + 'deg');
        img.style.setProperty('--src-product-delay', (0.12 + i * 0.06) + 's');
        productHost.appendChild(img);
      }

      shopLink.href = card.dataset.link || '#';
      shopLink.style.setProperty('--src-shop-bg', (useMobile && card.dataset.btnBgMobile ? card.dataset.btnBgMobile : card.dataset.btnBg) || '');
      shopLink.style.setProperty('--src-shop-text', (useMobile && card.dataset.btnTextMobile ? card.dataset.btnTextMobile : card.dataset.btnText) || '');
      shopLink.style.setProperty('--src-shop-border', (useMobile && card.dataset.btnBorderMobile ? card.dataset.btnBorderMobile : card.dataset.btnBorder) || '');
      detailTrigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      root.classList.add('is-detail-open');
      overlay.setAttribute('aria-hidden', 'false');
      lockPageScroll();
      document.addEventListener('keydown', trapDetailFocus);
      if (closeButton) closeButton.focus();
    }

    function closeDetail() {
      root.classList.remove('is-detail-open');
      overlay.setAttribute('aria-hidden', 'true');
      unlockPageScroll();
      document.removeEventListener('keydown', trapDetailFocus);
      if (detailTrigger && document.contains(detailTrigger)) detailTrigger.focus();
      detailTrigger = null;
      if (root.classList.contains('is-expanded')) startAutoplay();
    }

    function selectCard(card) {
      const index = parseInt(card.dataset.cardIndex, 10);
      if (isNaN(index)) return;

      // Stack mode has no fan to rotate, so open the detail straight away. Ali, 19 Aug: with fewer
      // brands "they just see that first sequence of the card sliding in and then, if you hover and
      // click, it does the card pop up thing". The original returned early here, which meant the
      // detail view was unreachable unless the fan was expanded.
      if (!root.classList.contains('is-expanded')) {
        openDetail(card);
        return;
      }

      if (index !== activeIndex) {
        activeIndex = index;
        renderFan();
        window.setTimeout(function () {
          openDetail(card);
        }, 520);
      } else {
        openDetail(card);
      }
    }

    cards.forEach(function (card) {
      card.addEventListener('click', function () {
        // A swipe on the fan shouldn't open the detail it happens to end on.
        if (suppressClick) return;
        selectCard(card);
      });
      card.addEventListener('mouseenter', function () {
        setInitialActive(card);
      });
      card.addEventListener('focusin', function () {
        setInitialActive(card);
      });
    });

    if (deck) {
      deck.addEventListener('mouseleave', clearInitialActive);
      deck.addEventListener('focusout', function (event) {
        if (!deck.contains(event.relatedTarget)) clearInitialActive();
      });
    }

    // Swipe the expanded fan one card at a time. Listeners live on the stage so
    // both the cards and the gaps between them respond. Passive (no
    // preventDefault) so vertical page scroll stays smooth.
    function fanStep(delta) {
      if (!cards.length) return;
      activeIndex = (activeIndex + delta + cards.length) % cards.length;
      renderFan();
    }

    function scheduleAutoplayResume() {
      window.clearTimeout(swipeResumeTimer);
      swipeResumeTimer = window.setTimeout(startAutoplay, 4500);
    }

    if (stage) {
      stage.addEventListener('touchstart', function (event) {
        if (!root.classList.contains('is-expanded') || root.classList.contains('is-detail-open')) return;
        if (event.touches.length !== 1) return;
        const touch = event.touches[0];
        swipeStartX = touch.clientX;
        swipeStartY = touch.clientY;
        swiping = true;
        stopAutoplay();
        window.clearTimeout(swipeResumeTimer);
      }, { passive: true });

      stage.addEventListener('touchend', function (event) {
        if (!swiping) return;
        swiping = false;
        const touch = event.changedTouches[0];
        const dx = touch.clientX - swipeStartX;
        const dy = touch.clientY - swipeStartY;
        // Horizontal-dominant drag past the threshold = move one card.
        if (Math.abs(dx) > 36 && Math.abs(dx) > Math.abs(dy) * 1.2) {
          suppressClick = true;
          window.setTimeout(function () { suppressClick = false; }, 450);
          fanStep(dx < 0 ? 1 : -1);
        }
        scheduleAutoplayResume();
      }, { passive: true });

      stage.addEventListener('touchcancel', function () {
        swiping = false;
        scheduleAutoplayResume();
      }, { passive: true });
    }

    // The expand button only exists when there are enough brands for the fan, so guard the binding.
    if (expandButton) expandButton.addEventListener('click', function () {
      if (root.classList.contains('is-expanded')) return;
      window.clearTimeout(dealTimer);
      clearInitialActive();
      root.classList.add('is-expanded');
      root.classList.add('is-dealt', 'is-dealt-complete');
      expandButton.setAttribute('aria-expanded', 'true');
      // Centre the fan on the middle initial card, so the dealt 5 settle into
      // the central fan slots rather than flying off to one side.
      const initialCount = cards.filter(function (c) { return c.dataset.initial === 'true'; }).length || cards.length;
      activeIndex = Math.floor(initialCount / 2);
      renderFan();
      startAutoplay();
    });

    closeButton.addEventListener('click', closeDetail);
    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) closeDetail();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && root.classList.contains('is-detail-open')) {
        closeDetail();
      }
    });

    // Theme editor: selecting a brand block auto-opens its detail card so the
    // secondary colour, logo and product images can be previewed while editing.
    if (window.Shopify && window.Shopify.designMode) {
      root.addEventListener('shopify:block:select', function (event) {
        const card = event.target && event.target.closest && event.target.closest('[data-src-card]');
        if (card) openDetail(card);
      });
      root.addEventListener('shopify:block:deselect', closeDetail);
    }

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            dealInitialCards();
          } else {
            // Section scrolled out of view: animate the cards back out so they
            // can be re-dealt when it returns (unless the fan was opened).
            resetInitialCards();
          }
        });
      }, { threshold: 0.12 });

      observer.observe(root);
    } else {
      dealInitialCards();
    }
};

const bootStackedRotateCards = () =>
  document.querySelectorAll('stacked-rotate-cards').forEach(initStackedRotateCards);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootStackedRotateCards, { once: true });
} else {
  bootStackedRotateCards();
}

document.addEventListener('shopify:section:load', bootStackedRotateCards);
